import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Eye, Cloud, FileText, Users } from 'lucide-react';
import { leadService } from '../../../services/api';

export default function StaleLeadsWidget() {
  const navigate = useNavigate();
  const [staleLeads, setStaleLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStaleLeads();
  }, []);

  const loadStaleLeads = async () => {
    try {
      setLoading(true);
      
      // Get all leads from all categories
      const [blueSky, proposal, negotiation] = await Promise.all([
        leadService.getBlueSkyLeads({ page: 1, limit: 1000 }),
        leadService.getProposalPhaseLeads({ page: 1, limit: 1000 }),
        leadService.getNegotiationLeads({ page: 1, limit: 1000 })
      ]);

      // Combine and filter stale leads (60+ days)
      const allLeads = [
        ...blueSky.data.data.leads.map(l => ({ ...l, category: 'Blue Sky', icon: Cloud, color: 'cyan' })),
        ...proposal.data.data.leads.map(l => ({ ...l, category: 'Proposal', icon: FileText, color: 'amber' })),
        ...negotiation.data.data.leads.map(l => ({ ...l, category: 'Negotiation', icon: Users, color: 'green' }))
      ];

      const stale = allLeads
        .filter(lead => lead.days_active > 60)
        .sort((a, b) => b.days_active - a.days_active)
        .slice(0, 10); // Top 10 most stale

      setStaleLeads(stale);
    } catch (error) {
      console.error('Error loading stale leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryRoute = (category) => {
    switch(category) {
      case 'Blue Sky': return '/leads/blue-sky';
      case 'Proposal': return '/leads/proposal-phase';
      case 'Negotiation': return '/leads/negotiation';
      default: return '/admin/leads';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Stale Leads Alert</h3>
            <p className="text-sm text-slate-400">Leads needing immediate attention</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-3 border-red-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Stale Leads Alert</h3>
            <p className="text-sm text-slate-400">
              {staleLeads.length} lead{staleLeads.length !== 1 ? 's' : ''} inactive for 60+ days
            </p>
          </div>
        </div>
      </div>

      {staleLeads.length > 0 ? (
        <div className="space-y-2">
          {staleLeads.map((lead, index) => {
            const Icon = lead.icon;
            return (
              <div
                key={index}
                className="bg-slate-800 hover:bg-slate-700 rounded-lg p-3 transition-colors cursor-pointer group"
                onClick={() => navigate(`/admin/leads/view/${lead.lead_id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <Icon className={`w-4 h-4 text-${lead.color}-400`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{lead.organisation_name}</p>
                      <p className="text-slate-400 text-xs">{lead.consultant_name || 'Unassigned'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-red-400 font-bold text-sm">{lead.days_active} days</p>
                      <p className="text-slate-500 text-xs">{formatCurrency(lead.estimated_revenue || 0)}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/leads/view/${lead.lead_id}`);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-all"
                    >
                      <Eye className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <AlertTriangle className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400">No stale leads - great job! 🎉</p>
          <p className="text-slate-500 text-sm mt-1">All leads have been followed up within 60 days</p>
        </div>
      )}

      {staleLeads.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-800">
          <button
            onClick={() => navigate('/leads/blue-sky')}
            className="w-full px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-semibold text-sm transition-colors"
          >
            View All Stale Leads
          </button>
        </div>
      )}
    </div>
  );
}